import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { ArrowLeft, Plus, Pencil, Trash2, Gift, Heart, Copy, ExternalLink, Users, Check, X as XIcon } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatEUR = (n) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(n || 0);
const formatDate = (s) => { if (!s) return '—'; try { return new Date(s).toLocaleDateString('it-IT'); } catch { return s; } };

const EMPTY = { type: 'wedding', title: '', description: '', event_date: '', client_id: '', goal_amount: '', thank_you_message: '', cover_image_url: '' };

const GiftLists = () => {
  const { user } = useAuth();
  const [lists, setLists] = useState([]);
  const [clients, setClients] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [detail, setDetail] = useState(null); // {list, contributions[], totals}
  const [confirmDialog, setConfirmDialog] = useState(null); // contribution being confirmed
  const [confirmedAmount, setConfirmedAmount] = useState('');
  const [confirmNotes, setConfirmNotes] = useState('');

  useEffect(() => { fetchLists(); fetchClients(); }, []);

  const fetchLists = async () => {
    try { const r = await axios.get(`${API}/gift-lists`); setLists(r.data || []); }
    catch (e) { toast.error('Errore caricamento liste'); }
  };
  const fetchClients = async () => {
    try {
      const r = await axios.get(`${API}/users`);
      setClients((r.data || []).filter(u => u.role === 'client'));
    } catch {}
  };
  const openCreate = () => { setEditingId(null); setForm(EMPTY); setShowForm(true); };
  const openEdit = (l) => {
    setEditingId(l.id);
    setForm({ ...EMPTY, ...l, event_date: l.event_date ? new Date(l.event_date).toISOString().slice(0,10) : '', goal_amount: l.goal_amount || '' });
    setShowForm(true);
  };
  const save = async () => {
    if (!form.title.trim() || !form.client_id) { toast.error('Titolo e cliente sono obbligatori'); return; }
    const payload = {
      ...form,
      event_date: form.event_date ? new Date(form.event_date).toISOString() : null,
      goal_amount: form.goal_amount ? parseFloat(form.goal_amount) : null,
    };
    try {
      if (editingId) await axios.put(`${API}/gift-lists/${editingId}`, payload);
      else await axios.post(`${API}/gift-lists`, payload);
      toast.success(editingId ? 'Lista aggiornata' : 'Lista creata');
      setShowForm(false); fetchLists();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore'); }
  };
  const del = async (id) => {
    if (!window.confirm('Eliminare questa lista e tutti i contributi?')) return;
    try { await axios.delete(`${API}/gift-lists/${id}`); toast.success('Eliminata'); fetchLists(); } catch { toast.error('Errore'); }
  };
  const toggleActive = async (l) => {
    try { await axios.put(`${API}/gift-lists/${l.id}`, { is_active: !l.is_active }); fetchLists(); } catch { toast.error('Errore'); }
  };
  const copyPublicLink = (slug) => {
    const url = `${window.location.origin}/gift/${slug}`;
    navigator.clipboard.writeText(url);
    toast.success('Link copiato');
  };
  const openDetail = async (l) => {
    try {
      const r = await axios.get(`${API}/gift-lists/${l.id}/contributions`);
      setDetail({ list: l, ...r.data });
    } catch { toast.error('Errore caricamento contributi'); }
  };
  const openConfirm = (c) => {
    setConfirmDialog(c);
    setConfirmedAmount(c.confirmed_amount ?? c.declared_amount);
    setConfirmNotes(c.admin_notes || '');
  };
  const doConfirm = async () => {
    if (!confirmDialog) return;
    try {
      await axios.put(
        `${API}/gift-lists/${confirmDialog.gift_list_id}/contributions/${confirmDialog.id}/confirm`,
        { confirmed_amount: parseFloat(confirmedAmount), admin_notes: confirmNotes }
      );
      toast.success('Contributo confermato');
      setConfirmDialog(null);
      openDetail(detail.list);
    } catch { toast.error('Errore conferma'); }
  };
  const unconfirm = async (c) => {
    try {
      await axios.put(`${API}/gift-lists/${c.gift_list_id}/contributions/${c.id}/unconfirm`);
      openDetail(detail.list);
    } catch { toast.error('Errore'); }
  };
  const delContrib = async (c) => {
    if (!window.confirm('Eliminare questo contributo?')) return;
    try {
      await axios.delete(`${API}/gift-lists/${c.gift_list_id}/contributions/${c.id}`);
      openDetail(detail.list);
    } catch { toast.error('Errore'); }
  };

  if (!['admin','agent'].includes(user?.role)) {
    return <div className="min-h-screen flex items-center justify-center"><Card><CardHeader><CardTitle>Accesso negato</CardTitle></CardHeader></Card></div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-pink-50" data-testid="gift-lists-page">
      <header className="bg-white shadow-sm border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/dashboard"><Button variant="outline" size="sm"><ArrowLeft className="w-4 h-4 mr-1" />Dashboard</Button></Link>
            <div className="flex items-center gap-2"><Heart className="w-6 h-6 text-pink-500" /><h1 className="text-lg sm:text-xl font-bold text-slate-800">Liste Nozze / Regalo</h1></div>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        <Card>
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
              <div><CardTitle>Le liste</CardTitle><CardDescription>Crea liste nozze/regalo con link condivisibile</CardDescription></div>
              <Button onClick={openCreate} className="bg-pink-600 hover:bg-pink-700" data-testid="add-gift-list-btn"><Plus className="w-4 h-4 mr-1" />Nuova Lista</Button>
            </div>
          </CardHeader>
          <CardContent>
            {lists.length === 0 ? (
              <div className="text-center py-12 text-slate-500"><Gift className="w-12 h-12 mx-auto mb-3 text-slate-300" /><p className="font-medium">Nessuna lista</p></div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {lists.map((l) => (
                  <div key={l.id} data-testid={`gift-list-${l.id}`} className="border border-slate-200 rounded-lg p-4 bg-white hover:shadow-md transition">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          {l.type === 'wedding' ? <Heart className="w-4 h-4 text-pink-500" /> : <Gift className="w-4 h-4 text-indigo-500" />}
                          <h4 className="font-semibold text-slate-800 truncate">{l.title}</h4>
                          <Badge variant={l.is_active ? 'default' : 'outline'} className="text-xs">{l.is_active ? 'Attiva' : 'Nascosta'}</Badge>
                        </div>
                        <div className="text-xs text-slate-500 mt-1">
                          Cliente: <strong>{l.client_name}</strong>
                          {l.event_date && ` · Evento ${formatDate(l.event_date)}`}
                          {l.goal_amount && ` · Obiettivo ${formatEUR(l.goal_amount)}`}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      <Button size="sm" variant="outline" onClick={() => copyPublicLink(l.public_slug)} data-testid={`copy-link-${l.id}`}>
                        <Copy className="w-3.5 h-3.5 mr-1" />Copia link
                      </Button>
                      <a href={`/gift/${l.public_slug}`} target="_blank" rel="noreferrer">
                        <Button size="sm" variant="outline"><ExternalLink className="w-3.5 h-3.5" /></Button>
                      </a>
                      <Button size="sm" variant="outline" onClick={() => openDetail(l)} data-testid={`view-contribs-${l.id}`}>
                        <Users className="w-3.5 h-3.5 mr-1" />Contributi
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => toggleActive(l)}>{l.is_active ? 'Pausa' : 'Attiva'}</Button>
                      <Button size="sm" variant="outline" onClick={() => openEdit(l)}><Pencil className="w-3.5 h-3.5 text-blue-600" /></Button>
                      <Button size="sm" variant="outline" onClick={() => del(l.id)}><Trash2 className="w-3.5 h-3.5 text-red-600" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Create/Edit dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="gift-form-dialog">
          <DialogHeader><DialogTitle>{editingId ? 'Modifica Lista' : 'Nuova Lista'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><Label>Tipo *</Label>
              <Select value={form.type} onValueChange={(v) => setForm({...form, type: v})}>
                <SelectTrigger data-testid="gift-form-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="wedding">Lista Nozze</SelectItem>
                  <SelectItem value="gift">Lista Regalo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Cliente destinatario *</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm({...form, client_id: v})}>
                <SelectTrigger data-testid="gift-form-client"><SelectValue placeholder="Seleziona cliente" /></SelectTrigger>
                <SelectContent>
                  {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name} · {c.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2"><Label>Titolo *</Label>
              <Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} placeholder="Es. Lista Nozze Marco e Sara" data-testid="gift-form-title" />
            </div>
            <div><Label>Data evento</Label>
              <Input type="date" value={form.event_date} onChange={(e) => setForm({...form, event_date: e.target.value})} />
            </div>
            <div><Label>Obiettivo (€) opzionale</Label>
              <Input type="number" value={form.goal_amount} onChange={(e) => setForm({...form, goal_amount: e.target.value})} placeholder="5000" />
            </div>
            <div className="md:col-span-2"><Label>Descrizione</Label>
              <Textarea rows={3} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} placeholder="Il nostro viaggio di nozze..." />
            </div>
            <div className="md:col-span-2"><Label>Immagine copertina (URL)</Label>
              <Input value={form.cover_image_url} onChange={(e) => setForm({...form, cover_image_url: e.target.value})} placeholder="https://..." />
            </div>
            <div className="md:col-span-2"><Label>Messaggio di ringraziamento (visibile agli ospiti dopo la contribuzione)</Label>
              <Textarea rows={2} value={form.thank_you_message} onChange={(e) => setForm({...form, thank_you_message: e.target.value})} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Annulla</Button>
            <Button onClick={save} className="bg-pink-600 hover:bg-pink-700" data-testid="save-gift-list-btn">{editingId ? 'Salva' : 'Crea'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail / contributions dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="gift-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {detail.list.type === 'wedding' ? <Heart className="w-5 h-5 text-pink-500" /> : <Gift className="w-5 h-5 text-indigo-500" />}
                  {detail.list.title}
                </DialogTitle>
                <DialogDescription>Cliente: {detail.list.client_name}</DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-3 gap-3 py-2">
                <div className="bg-slate-50 border border-slate-200 rounded p-3 text-center">
                  <div className="text-xs text-slate-500 uppercase">Contributi</div>
                  <div className="text-xl font-bold text-slate-800">{detail.count}</div>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded p-3 text-center">
                  <div className="text-xs text-blue-600 uppercase">Dichiarato</div>
                  <div className="text-xl font-bold text-blue-800">{formatEUR(detail.declared_total)}</div>
                </div>
                <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-center">
                  <div className="text-xs text-emerald-600 uppercase">Confermato</div>
                  <div className="text-xl font-bold text-emerald-800">{formatEUR(detail.confirmed_total)}</div>
                </div>
              </div>
              <div className="space-y-2">
                {detail.contributions.length === 0 ? (
                  <p className="text-center text-slate-500 py-6">Nessun contributo</p>
                ) : detail.contributions.map((c) => (
                  <div key={c.id} className={`border rounded p-3 ${c.is_confirmed ? 'border-emerald-200 bg-emerald-50/50' : 'border-slate-200'}`} data-testid={`contrib-${c.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <strong>{c.guest_name}</strong>
                          {c.is_confirmed ? (
                            <Badge className="bg-emerald-600 text-xs">Confermato {formatEUR(c.confirmed_amount)}</Badge>
                          ) : (
                            <Badge variant="outline" className="text-xs">Dichiarato {formatEUR(c.declared_amount)}</Badge>
                          )}
                        </div>
                        <div className="text-xs text-slate-500 mt-1">
                          {c.guest_email && <span>{c.guest_email}</span>}
                          {c.guest_phone && <span> · {c.guest_phone}</span>}
                          <span> · {formatDate(c.created_at)}</span>
                        </div>
                        {c.message && <div className="text-sm text-slate-700 italic mt-1">&ldquo;{c.message}&rdquo;</div>}
                        {c.admin_notes && <div className="text-xs text-slate-500 mt-1">Note admin: {c.admin_notes}</div>}
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        {c.is_confirmed ? (
                          <Button size="sm" variant="outline" onClick={() => unconfirm(c)} title="Annulla conferma"><XIcon className="w-4 h-4 text-amber-600" /></Button>
                        ) : (
                          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => openConfirm(c)} data-testid={`confirm-${c.id}`}>
                            <Check className="w-4 h-4 mr-1" />Conferma
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => delContrib(c)}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <DialogFooter><Button variant="outline" onClick={() => setDetail(null)}>Chiudi</Button></DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirm amount dialog */}
      <Dialog open={!!confirmDialog} onOpenChange={(o) => !o && setConfirmDialog(null)}>
        <DialogContent data-testid="confirm-amount-dialog">
          <DialogHeader>
            <DialogTitle>Conferma importo ricevuto</DialogTitle>
            <DialogDescription>
              {confirmDialog?.guest_name} ha dichiarato {formatEUR(confirmDialog?.declared_amount)}. Inserisci l&apos;importo effettivamente arrivato in banca.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Importo confermato (€) *</Label>
              <Input type="number" step="0.01" value={confirmedAmount} onChange={(e) => setConfirmedAmount(e.target.value)} data-testid="confirm-amount-input" />
            </div>
            <div>
              <Label>Note (opzionale)</Label>
              <Textarea rows={2} value={confirmNotes} onChange={(e) => setConfirmNotes(e.target.value)} placeholder="Es. Bonifico ricevuto il 15/09, CRO..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDialog(null)}>Annulla</Button>
            <Button onClick={doConfirm} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-confirm-btn">Conferma</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GiftLists;
